import { z } from 'zod';

/**
 * Company Validation Schemas
 */

// Basic company info schema
export const companyBasicSchema = z.object({
  name: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(200, 'Company name must be less than 200 characters'),
  registrationNumber: z
    .string()
    .min(1, 'Registration number is required')
    .max(50, 'Registration number must be less than 50 characters'),
  vatNumber: z
    .string()
    .max(50, 'VAT number must be less than 50 characters')
    .optional()
    .or(z.literal('')),
  tag: z
    .string()
    .max(50, 'Tag must be less than 50 characters')
    .optional()
    .or(z.literal('')),
});

// Address schema (reusable)
export const addressSchema = z.object({
  address: z
    .string()
    .max(500, 'Address must be less than 500 characters')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .max(100, 'City must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  province: z
    .string()
    .max(100, 'Province must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  postalCode: z
    .string()
    .max(20, 'Postal code must be less than 20 characters')
    .optional()
    .or(z.literal('')),
  country: z
    .string()
    .max(100, 'Country must be less than 100 characters')
    .optional()
    .or(z.literal('')),
});

// Contact schema (reusable)
export const contactSchema = z.object({
  contactPerson: z
    .string()
    .max(100, 'Contact person must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  contactNumber: z
    .string()
    .max(50, 'Contact number must be less than 50 characters')
    .optional()
    .or(z.literal('')),
  emailAddress: z
    .union([
      z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address'),
      z.literal('')
    ])
    .optional(),
  secondaryContact: z
    .string()
    .max(200, 'Secondary contact must be less than 200 characters')
    .optional()
    .or(z.literal('')),
});

// Billing schema
export const billingSchema = z.object({
  billingContactName: z
    .string()
    .max(100, 'Billing contact name must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  billingEmail: z
    .union([
      z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid billing email'),
      z.literal('')
    ])
    .optional(),
  billingAddress: z
    .string()
    .max(500, 'Billing address must be less than 500 characters')
    .optional()
    .or(z.literal('')),
  paymentTerms: z
    .enum(['7 Days', '14 Days', '30 Days', 'Month End'])
    .default('7 Days'),
});

// Branch planning schema
export const branchPlanningSchema = z.object({
  numberOfBranches: z
    .number()
    .int()
    .min(0, 'Number of branches cannot be negative')
    .default(0),
});

// Full company schema for wizard
export const companyWizardSchema = companyBasicSchema
  .merge(addressSchema)
  .merge(contactSchema)
  .merge(billingSchema)
  .merge(branchPlanningSchema)
  .extend({
    technicianIds: z.array(z.number()).default([]),
  });

// Simple company form schema
export const companyFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Company name must be at least 2 characters'),
  registrationNumber: z
    .string()
    .min(1, 'Registration number is required'),
  sectorId: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      if (val == null) return undefined;
      if (typeof val === 'string' && val.trim() === '') return undefined;
      const num = Number(val);
      return Number.isFinite(num) && num > 0 ? num : undefined;
    }),
  numberOfBranches: z
    .union([z.string(), z.number()])
    .transform((val) => (typeof val === 'string' ? parseInt(val, 10) || 0 : val)),
  tag: z.string().optional().or(z.literal('')),
});

export default companyWizardSchema;

